/**
 * ONE hour, cleanly: the hour, its rank (or why it is unranked) and score up
 * top; how the score is made (a small table, one row per range); every value
 * of the hour on the first range, grouped like the Flow Optimizer (traffic,
 * conversions, money, rates, advanced); and what this container does in that
 * hour — and what the hour rules said about it (Phase 19). Shows the
 * hovered hour, the pinned one, or the best by default.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { type DropGenHourFormula } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

import { dkLabel } from '../../../../utils/clock/dk-label';
import { excludeHit } from '../../rules/rule-model';
import { ExcludedMark, RuleHitList } from '../../rules/RuleHits';

import { cphHourRange, hourRange, minuteLabel, scoreLabel, type HourView } from './hour-chart-math';
import { HourScoreBreakdown } from './HourScoreBreakdown';
import { HourAllValues } from './values/HourAllValues';
import { HourTagChips } from './tags';

interface Props {
  row: HourView;
  rankedHours: number;
  /** What the hours are ranked on, in words — "CR 100 over last 14 days". */
  formulaLabel: string;
  pinned: boolean;
  /** The formula the hours were ranked on — blocks read as math, not references. */
  formula?: DropGenHourFormula;
  /** The country's timezone — the hour is echoed on the Danish clock. */
  timezone?: string;
  /** The country's flag, leading the local hour. */
  countryFlag?: string;
  /** False on a master config — it has no hours of its own, the window is the reference. */
  hasContainer?: boolean;
  /** Hour tag colours (Tag Rules › Hours). */
  tagColors?: Record<string, string>;
}

const Section: FC<{ title: string; note?: string; children: ReactNode }> = ({ title, note, children }) => (
  <div className="flex flex-col gap-1.5 border-t border-border/60 px-3 py-2.5">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
      {note ? <span className="ml-2 font-normal normal-case tracking-normal">{note}</span> : null}
    </span>
    {children}
  </div>
);

/** Where the hour sits for this editor — the container's hours, or (a master config) the country's window. */
function placeLine(row: HourView, hasContainer: boolean): string {
  if (!row.inWindow) return 'outside the country’s window — no drop goes here (a hard limit)';
  if (!hasContainer) return 'inside the country’s window';
  return row.inSpan ? 'inside this container’s hours' : 'outside this container’s hours — the optimizer never drops here';
}

/** " · slots at 08:00 🇩🇰 · 🇦🇺 17:00" — Danish first, like every other time (QA 18/10/2026). */
function slotsLine(slots: number[], timezone?: string, flag?: string): string {
  if (slots.length === 0) return '';
  const local = slots.map(minuteLabel).join(', ');
  const dk = timezone ? slots.map((m) => dkLabel(m, timezone)).join(', ') : null;
  const at = dk ? `${dk} 🇩🇰 · ${flag ? `${flag} ` : ''}${local}` : local;
  return ` · slot${slots.length > 1 ? 's' : ''} at ${at}`;
}

export const HourStatsCard: FC<Props> = ({ row, rankedHours, formulaLabel, pinned, formula, timezone, countryFlag, hasContainer = true, tagColors }) => (
  <div className={cn('overflow-hidden rounded-lg border bg-card', pinned ? 'border-primary/50' : 'border-border')}>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
      {timezone && cphHourRange(row.hour, timezone) ? (
        <span className="font-mono text-sm font-semibold tabular-nums" title="The same hour in Danish time, today">
          🇩🇰 {cphHourRange(row.hour, timezone)}
        </span>
      ) : null}
      <span className={cn('font-mono tabular-nums', timezone ? 'text-xs text-muted-foreground' : 'text-sm font-semibold')} title={timezone ? `${timezone} — the recipients' clock` : undefined}>
        {countryFlag ? `${countryFlag} ` : ''}
        {hourRange(row.hour)}
      </span>
      {row.unranked ? (
        <>
          {excludeHit(row.ruleHits) ? <ExcludedMark label /> : <Badge tone="secondary">not ranked</Badge>}
          <span className="text-xs text-muted-foreground">{row.excludedWhy} — not judged yet, never counted as bad</span>
        </>
      ) : (
        <>
          <Badge tone="info">#{row.rank} of {rankedHours} hours</Badge>
          {row.spanRank === 1 ? <Badge tone="success">its best hour — extras go here first</Badge> : null}
          {row.outsideLabel ? <Badge tone="secondary">{row.outsideLabel}</Badge> : null}
          <span className="text-xs text-muted-foreground">
            score <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{scoreLabel(row.score)}</span>
          </span>
        </>
      )}
      <span className="ml-auto text-[11px] text-muted-foreground">{pinned ? 'pinned — click the hour again to release' : 'hover an hour · click to pin'}</span>
    </div>
    {row.breakdown && row.breakdown.length > 0 ? (
      <Section title="How the score is made" note={formulaLabel}>
        <HourScoreBreakdown rows={row.breakdown} formula={formula} />
      </Section>
    ) : null}
    {row.ruleHits && row.ruleHits.length > 0 ? (
      <div className="border-t border-border/60 px-3 py-2.5">
        <RuleHitList hits={row.ruleHits} />
      </div>
    ) : null}
    {row.tags && Object.keys(row.tags).length > 0 ? (
      <Section title="Hour tags" note="Tag Rules › Hours · same campaign and clock">
        <HourTagChips tags={row.tags} colors={tagColors} />
      </Section>
    ) : null}
    <Section title="All values" note="first range">
      <HourAllValues bag={row.metrics ?? { visits: row.visits, uniqueVisits: row.uniqueVisits, clicks: row.clicks, conversions: row.conversions, revenue: row.revenue, cost: row.cost }} />
    </Section>
    <p className="border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
      {placeLine(row, hasContainer)}
      {slotsLine(row.slots, timezone, countryFlag)}
      {row.extra === 'extra' ? ' · ⚡ the optimizer plans an extra drop here today' : row.extra === 'probe' ? ' · ⚡ the optimizer plans a probe here today' : ''}
    </p>
  </div>
);
